import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { WalkthroughPopover } from '../../common/components';
import {
  RadioGroup,
  Radio,
  Tooltip,
  Icon,
  Slider
} from '@salesforce/design-system-react';

import { sendEvent } from '../../utilities/instrumentation';

import './EmailPerformance.scss';

export class EmailPerformance extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openRateWeight: 50,
      clickRateWeight: 50,
      unsubscribePenaltyFactor: 0,
      sliderTimer: null
    };
  }

  componentDidMount() {
    const {
      openRateWeight,
      clickRateWeight,
      unsubscribePenaltyFactor,
      advancedSliders
    } = this.props;
    if (advancedSliders) {
      this.setState(advancedSliders);
    } else {
      this.setState({
        openRateWeight: Math.round(openRateWeight * 100),
        clickRateWeight: Math.round(clickRateWeight * 100),
        unsubscribePenaltyFactor: Math.round(unsubscribePenaltyFactor * 100)
      });
    }
  }

  handleRadioChange(event) {
    const { value } = event.target;
    this.props.actions.updateDataFilter({ eventType: value });
    if (value === 'open') {
      this.props.actions.updateDataFilter({
        openRateWeight: 1,
        clickRateWeight: 0,
        unsubscribePenaltyFactor: 0
      });
    }
    if (value === 'click') {
      this.props.actions.updateDataFilter({
        openRateWeight: 0,
        clickRateWeight: 1,
        unsubscribePenaltyFactor: 0
      });
    }
    if (value === 'advanced') {
      if (this.props.advancedSliders) {
        this.props.actions.updateDataFilter({
          openRateWeight: this.state.openRateWeight / 100,
          clickRateWeight: this.state.clickRateWeight / 100,
          unsubscribePenaltyFactor: this.state.unsubscribePenaltyFactor / 100
        });
      } else {
        this.props.actions.updateDataFilter({
          openRateWeight: 0.5,
          clickRateWeight: 0.5,
          unsubscribePenaltyFactor: 0
        });
      }
    }
    sendEvent('changeSelection', 'optimizationObjective', value);
  }

  handleSliderChange(slider, e, v) {
    let newState = {};
    if (slider === 'openRateWeight') {
      newState[slider] = v.value;
      newState['clickRateWeight'] = 100 - v.value;
    } else if (slider === 'clickRateWeight') {
      newState[slider] = v.value;
      newState['openRateWeight'] = 100 - v.value;
    } else {
      newState[slider] = v.value;
    }
    this.setState(newState);

    // only update data filter if slider stops moving for 1s
    clearTimeout(this.state.sliderTimer);
    this.setState({
      sliderTimer: setTimeout(() => {
        //only send the last slider value after 1s
        sendEvent('advancedOptimizationObjectiveChange', slider, v.value);
        this.updateWeights();
      }, 1000)
    });
  }

  updateWeights() {
    const newSliderValues = { ...this.state };
    delete newSliderValues.sliderTimer;
    this.props.actions.updateAdvancedSliders(newSliderValues);
    this.props.actions.updateDataFilter({
      openRateWeight: this.state.openRateWeight / 100,
      clickRateWeight: this.state.clickRateWeight / 100,
      unsubscribePenaltyFactor: this.state.unsubscribePenaltyFactor / 100
    });
  }

  renderAdvancedSliders() {
    const { formatMessage } = this.props.intl;
    const unsubscribeTooltip = (
      <Tooltip
        align="top"
        content={formatMessage({
          id:
            'EmailPerformance.advancedSliders.unsubscribePenalty.labels.tooltip'
        })}
      >
        <button className="slds-button tooltip-trigger-button">
          <Icon
            assistiveText={{ label: 'More Information' }}
            category="utility"
            name="info"
            size="x-small"
            position="relative"
          />
        </button>
      </Tooltip>
    );
    return (
      <div className="selection-container">
        <div className="advanced-optimization-weights">
          <div className="slider-section">
            <h2 className="advanced-container-title">
              <FormattedMessage
                id="EmailPerformance.advancedSliders.openRateClickRate.title"
                values={{
                  metricText: (
                    <strong>
                      {formatMessage({
                        id:
                          'EmailPerformance.advancedSliders.openRateClickRate.metricText'
                      })}
                    </strong>
                  )
                }}
              />
            </h2>
            <div className="slider-container">
              <Slider
                className="advanced-slider"
                label={formatMessage({
                  id:
                    'EmailPerformance.advancedSliders.openRateClickRate.labels.openRate'
                })}
                min={0}
                max={100}
                value={this.state.openRateWeight}
                onChange={(e, v) =>
                  this.handleSliderChange('openRateWeight', e, v)
                }
              />
            </div>
            <div className="slider-container">
              <Slider
                className="advanced-slider"
                label={formatMessage({
                  id:
                    'EmailPerformance.advancedSliders.openRateClickRate.labels.clickRate'
                })}
                min={0}
                max={100}
                value={this.state.clickRateWeight}
                onChange={(e, v) =>
                  this.handleSliderChange('clickRateWeight', e, v)
                }
              />
            </div>
          </div>
          <div className="slider-section">
            <h2 className="advanced-container-title">
              <FormattedMessage
                id="EmailPerformance.advancedSliders.unsubscribePenalty.title"
                values={{
                  metricText: (
                    <strong>
                      {formatMessage({
                        id:
                          'EmailPerformance.advancedSliders.unsubscribePenalty.metricText'
                      })}
                    </strong>
                  )
                }}
              />
            </h2>
            <div className="slider-container">
              <Slider
                className="advanced-slider"
                label={
                  <FormattedMessage
                    id="EmailPerformance.advancedSliders.unsubscribePenalty.labels.unsubscribePenalty"
                    values={{
                      tooltip: unsubscribeTooltip
                    }}
                  />
                }
                min={0}
                max={100}
                value={this.state.unsubscribePenaltyFactor}
                onChange={(e, v) =>
                  this.handleSliderChange('unsubscribePenaltyFactor', e, v)
                }
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { eventType } = this.props;
    const slidersEnabled = eventType === 'advanced';
    const advancedTooltip = (
      <Tooltip
        align="top"
        content={formatMessage({ id: 'EmailPerformance.advanced.tooltip' })}
      >
        <button className="slds-button tooltip-trigger-button">
          <Icon
            assistiveText={{ label: 'More Information' }}
            category="utility"
            name="info"
            size="x-small"
            position="relative"
          />
        </button>
      </Tooltip>
    );
    const performanceValues = [
      {
        label: <FormattedMessage id="EmailPerformance.open" />,
        value: 'open'
      },
      {
        label: <FormattedMessage id="EmailPerformance.click" />,
        value: 'click'
      },
      {
        label: (
          <FormattedMessage
            id="EmailPerformance.advanced.title"
            values={{
              tooltip: advancedTooltip
            }}
          />
        ),
        value: 'advanced'
      }
    ];
    return (
      <div className="email-performance-container">
        <div className="email-performance">
          <h1 className="section-title">
            <FormattedMessage id="EmailPerformance.title" />
          </h1>
          <div className="metric-radios selection-container">
            <RadioGroup
              onChange={e => this.handleRadioChange(e)}
              name="emailPerformance"
            >
              {performanceValues.map(value => (
                <Radio
                  key={value.value}
                  id={value.value}
                  label={value.label}
                  value={value.value}
                  checked={this.props.eventType === value.value}
                  variant="base"
                />
              ))}
            </RadioGroup>
          </div>
          {slidersEnabled ? this.renderAdvancedSliders() : null}
        </div>
        <WalkthroughPopover
          step={this.props.ftux.steps.get('optimizationObjective')}
          numSteps={this.props.ftux.numSteps}
          position="left"
          actions={this.props.actions}
        />
      </div>
    );
  }
}

EmailPerformance.propTypes = {
  eventType: PropTypes.string,
  openRateWeight: PropTypes.number,
  clickRateWeight: PropTypes.number,
  unsubscribePenaltyFactor: PropTypes.number,
  advancedSliders: PropTypes.object,
  ftux: PropTypes.object,
  actions: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(EmailPerformance);
